import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import ScanResultModal from "@/components/ScanResultModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanLine, Camera, CameraOff, Volume2, VolumeX, Beer, Martini, X } from "lucide-react";
import { typeText } from "@/lib/ticketTypes";

const beep = (ok = true) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = ok ? 880 : 220;
    o.type = "sine";
    g.gain.value = 0.08;
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, ok ? 120 : 250);
  } catch (e) { /* ignore */ }
};

const DRINKS = [
  { key: "BEER",     label: "BEER",     Icon: Beer,    field: "additional_beer_remaining" },
  { key: "COCKTAIL", label: "COCKTAIL", Icon: Martini, field: "additional_cocktail_remaining" },
];

export default function ScanTicket() {
  const [value, setValue] = useState("");
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    const s = localStorage.getItem("fb_station") || `station-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("fb_station", s);
    return s;
  });
  const [sound, setSound] = useState(true);
  const [cameraOn, setCameraOn] = useState(false);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const readerRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const refocus = () => {
      if (document.activeElement !== inputRef.current && !result && !validation && !cameraOn) inputRef.current?.focus();
    };
    const t = setInterval(refocus, 500);
    return () => clearInterval(t);
  }, [result, validation, cameraOn]);

  const doValidate = async (barcode) => {
    if (!barcode || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scan", { barcode, session_id: sessionId });
      if (data.result === "READY") setValidation(data);
      else { setResult(data); if (sound) beep(false); }
    } catch (e) {
      setResult({ result: "INVALID", message: formatApiError(e) });
      if (sound) beep(false);
    } finally { setValue(""); setBusy(false); }
  };

  const doRedeem = async (drink_type) => {
    if (!validation) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scan/redeem", {
        barcode: validation.ticket.barcode_token, drink_type, session_id: sessionId,
      });
      setValidation(null);
      setResult(data);
      if (sound) beep(data.result === "SUCCESS");
      if (navigator.vibrate) navigator.vibrate(data.result === "SUCCESS" ? 60 : [80, 60, 80]);
    } catch (e) {
      setResult({ result: "INVALID", message: formatApiError(e) });
      if (sound) beep(false);
    } finally { setBusy(false); }
  };

  const onKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); doValidate(value); } };
  const dismiss = () => { setResult(null); setTimeout(() => inputRef.current?.focus(), 30); };
  const cancelSelect = () => { setValidation(null); setTimeout(() => inputRef.current?.focus(), 30); };

  useEffect(() => {
    if (!cameraOn) { try { readerRef.current?.reset(); } catch (e) {} readerRef.current = null; return; }
    let mounted = true;
    (async () => {
      try {
        const zx = await import("@zxing/library");
        const hints = new Map();
        hints.set(zx.DecodeHintType.POSSIBLE_FORMATS, [zx.BarcodeFormat.PDF_417, zx.BarcodeFormat.QR_CODE, zx.BarcodeFormat.CODE_128]);
        const reader = new zx.BrowserMultiFormatReader(hints);
        readerRef.current = reader;
        await reader.decodeFromVideoDevice(null, videoRef.current, (res) => {
          if (res && mounted) { setCameraOn(false); doValidate(res.getText()); }
        });
      } catch (e) { console.error("Camera failed", e); setCameraOn(false); }
    })();
    return () => { mounted = false; try { readerRef.current?.reset(); } catch (e) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOn]);

  const t = validation?.ticket;
  const additionalTotal = t ? (t.additional_beer_remaining + t.additional_cocktail_remaining) : 0;
  const credit = t ? (t.credit_cents || 0) / 100 : 0;
  const prices = validation?.prices || { BEER: 5, COCKTAIL: 10 };
  const sched = validation?.employee_schedule;
  const compAvail = t ? (t.ticket_type === "EMPLOYEE" ? (sched?.available_now || 0) : t.base_drinks_remaining) : 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="scan-page">
      <div className="text-center">
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Food Bazaar</div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tight">SCAN TICKET</h1>
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm font-bold">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> READY TO SCAN
        </div>
        <div className="mt-2 text-xs text-slate-500">Station: <span className="font-mono-fb text-slate-300">{sessionId}</span></div>
      </div>

      {!validation && (
        <div className="bg-[#131D33] rounded-2xl border border-slate-800 p-4 sm:p-6 md:p-8">
          <label className="text-xs tracking-widest uppercase text-slate-400 font-bold">Scan or enter ticket ID / barcode</label>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <Input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={onKeyDown}
              placeholder="e.g. REG-02K4M9 or scan barcode…"
              className="h-14 sm:h-16 text-base sm:text-xl md:text-2xl font-mono-fb bg-[#0B1120] border-slate-700 text-white tracking-wider w-full"
              autoFocus data-testid="scan-input" />
            <Button onClick={() => doValidate(value)} disabled={busy || !value}
              className="h-14 sm:h-16 px-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold rounded-xl w-full sm:w-auto"
              data-testid="scan-submit-btn"><ScanLine size={20} className="mr-2" /> Validate</Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setCameraOn((v) => !v)}
              className="bg-slate-900 border-slate-700 text-slate-200" data-testid="camera-toggle-btn">
              {cameraOn ? <CameraOff size={16} className="mr-2" /> : <Camera size={16} className="mr-2" />}
              {cameraOn ? "Stop Camera" : "Scan with Camera"}
            </Button>
            <Button variant="outline" onClick={() => setSound((s) => !s)}
              className="bg-slate-900 border-slate-700 text-slate-200" data-testid="sound-toggle-btn">
              {sound ? <Volume2 size={16} className="mr-2" /> : <VolumeX size={16} className="mr-2" />}
              Sound: {sound ? "On" : "Off"}
            </Button>
          </div>
          {cameraOn && (
            <div className="mt-4 rounded-xl overflow-hidden border border-slate-700 bg-black">
              <video ref={videoRef} className="w-full h-64 object-cover" data-testid="camera-video" />
            </div>
          )}
        </div>
      )}

      {validation && t && (
        <div className="bg-[#131D33] rounded-2xl border-2 border-emerald-500/40 p-6 sm:p-8 fb-pop" data-testid="drink-select-panel">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-emerald-400 font-bold">Ticket validated</div>
              <div className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight mt-1">
                {t.ticket_id} · <span className={typeText(t.ticket_type)}>{t.ticket_type}</span>
              </div>
              {t.ticket_type === "EMPLOYEE" && (
                <div className="mt-1 text-sm text-amber-300 font-bold tracking-wide" data-testid="employee-ticket-info">
                  EMPLOYEE TICKET · Included Drinks: {t.base_drinks} · Drinks Remaining: {t.drinks_remaining} · Available now: {compAvail}
                  {sched?.next_unlock && <span className="block text-amber-200/80 font-normal">Next drink unlocks at {sched.next_unlock} (event time {sched.event_time})</span>}
                </div>
              )}
              {t.guest_name && <div className="text-slate-300">{t.guest_name}</div>}
            </div>
            <Button variant="outline" onClick={cancelSelect} className="bg-slate-900 border-slate-700 text-slate-200" data-testid="drink-select-cancel">
              <X size={16} className="mr-1" /> Cancel
            </Button>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-900/70 border border-slate-800 p-4">
              <div className="text-[10px] tracking-widest uppercase text-slate-400 font-bold">Included Drinks Remaining</div>
              <div className="font-display text-5xl font-black text-white" data-testid="included-remaining">{t.base_drinks_remaining}</div>
              <div className="text-[11px] text-slate-500 mt-1">1 per drink · Beer or Cocktail · used first</div>
            </div>
            <div className="rounded-xl bg-orange-500/10 border border-orange-500/40 p-4">
              <div className="text-[10px] tracking-widest uppercase text-orange-300 font-bold">Purchased Credit</div>
              <div className="font-display text-4xl font-black text-orange-200" data-testid="credit-balance">${credit.toFixed(2)}</div>
              {additionalTotal > 0 && (
                <div className="mt-1 space-y-0.5 text-xs">
                  {t.additional_beer_remaining > 0 && <div className="text-orange-200 font-semibold" data-testid="additional-beer">+ BEER × {t.additional_beer_remaining} (earlier purchase)</div>}
                  {t.additional_cocktail_remaining > 0 && <div className="text-orange-200 font-semibold" data-testid="additional-cocktail">+ COCKTAIL × {t.additional_cocktail_remaining} (earlier purchase)</div>}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 text-center">
            <div className="text-xs tracking-[0.3em] uppercase text-slate-400">Included Drinks Left</div>
            <div className="font-display text-6xl sm:text-7xl font-black text-white leading-none" data-testid="drink-select-remaining">
              {t.drinks_remaining}
            </div>
          </div>

          <div className="mt-6">
            <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold text-center">Select Drink</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {DRINKS.map((d) => {
                const perType = t[d.field] || 0;
                const byCredit = compAvail <= 0 && perType <= 0 && credit >= prices[d.key];
                const available = compAvail > 0 || perType > 0 || (t.ticket_type !== "EMPLOYEE" && credit >= prices[d.key]);
                const fromAdditional = compAvail <= 0 && (perType > 0 || byCredit);
                const cls = fromAdditional
                  ? "from-orange-500 to-amber-500"
                  : (d.key === "BEER" ? "from-amber-500 to-orange-500" : "from-rose-600 to-pink-600");
                return (
                  <button key={d.key} onClick={() => doRedeem(d.key)}
                    disabled={busy || !available}
                    data-testid={`drink-select-${d.key.toLowerCase()}`}
                    className={`group ${available ? `bg-gradient-to-br ${cls} hover:brightness-110 active:scale-[0.98]` : "bg-slate-800 opacity-40 cursor-not-allowed"} transition-all rounded-2xl p-6 flex flex-col items-center justify-center text-white shadow-lg`}
                  >
                    <d.Icon size={44} strokeWidth={2} />
                    <div className="mt-2 font-display text-2xl font-black tracking-tight">{d.label} · ${prices[d.key]}</div>
                    {compAvail > 0 && <div className="mt-1 text-[10px] tracking-widest font-bold bg-black/20 px-2 py-0.5 rounded">INCLUDED</div>}
                    {compAvail <= 0 && perType > 0 && <div className="mt-1 text-[10px] tracking-widest font-bold text-orange-100 bg-black/20 px-2 py-0.5 rounded">+{perType} PURCHASED</div>}
                    {byCredit && <div className="mt-1 text-[10px] tracking-widest font-bold text-orange-100 bg-black/20 px-2 py-0.5 rounded">-${prices[d.key]} CREDIT</div>}
                    {!available && <div className="mt-1 text-[10px] text-slate-400">NOT AVAILABLE</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <ScanResultModal result={result} onDismiss={dismiss} />
    </div>
  );
}
